<!-- src/components/AdvanceForm.vue -->

<script lang="ts">
import { defineComponent, ref } from 'vue'
import type { Advance } from '@/composables/useAdvances'
import { useAdvances } from '@/composables/useAdvances'

export default defineComponent({
  name: 'AdvanceForm',
  props: {
    advanceIds: {
      type: Array as () => number[],
      required: true
    }
  },

  setup() {
    const id = ref('')
    const name = ref('')
    const description = ref('')
    const status = ref('')
    const doc = ref('')
    const test = ref('')
    const requires = ref('')
    const required_by = ref('')
    const due_date = ref('')
    const complete = ref('')

    const { createAdvance } = useAdvances()

    const submitForm = async () => {
      if (name.value) {
        let parsedDoc = null
        let parsedTest = null
        try {
          if (doc.value) parsedDoc = JSON.parse(doc.value)
        } catch (e) {
          alert('Invalid JSON in Doc field')
          return
        }
        try {
          if (test.value) parsedTest = JSON.parse(test.value)
        } catch (e) {
          alert('Invalid JSON in Test field')
          return
        }

        // Convert due_date to YYYY-MM-DD format if it has a value
        const formattedDueDate = due_date.value
          ? new Date(due_date.value).toISOString().split('T')[0]
          : null

        // Create advance data without blank or undefined fields
        const data: Advance = {
          name: name.value,
          description: description.value || undefined,
          status: status.value || undefined,
          doc: parsedDoc || undefined,
          test: parsedTest || undefined,
          requires: requires.value
            ? requires.value
                .split(',')
                .map((id) => id.trim())
                .filter((id) => id)
            : undefined,
          required_by: required_by.value
            ? required_by.value
                .split(',')
                .map((id) => id.trim())
                .filter((id) => id)
            : undefined,
          due_date: formattedDueDate || undefined,
          complete: complete.value || undefined
        }

        // Add 'name' property to the data object
        if (!data.name) {
          alert('Please fill in the Name field')
          return
        }

        // Remove undefined fields from the data object
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const cleanData = Object.fromEntries(
          Object.entries(data).filter(([_, v]) => v !== undefined)
        )

        if (!cleanData.name) {
          console.error('Name is required')
          return
        }

        await createAdvance(cleanData as Advance)

        // Optionally, you can reset the form fields after submission
        id.value = ''
        name.value = ''
        description.value = ''
        status.value = ''
        doc.value = ''
        test.value = ''
        requires.value = ''
        required_by.value = ''
        due_date.value = ''
        complete.value = ''
      } else {
        alert('Please fill in all fields')
      }
    }

    return {
      id,
      name,
      description,
      status,
      doc,
      test,
      requires,
      required_by,
      due_date,
      complete,
      submitForm
    }
  }
})
</script>

<template>
  <div>
    <h1>Advance Form</h1>
    <form @submit.prevent="submitForm">
      <!-- Select box for ID -->
      <div>
        <label for="id">ID:</label>
        <select id="id" v-model="id">
          <option value="">Select an ID</option>
          <!-- Iterate over advanceIds to populate options -->
          <option v-for="advanceId in advanceIds" :key="advanceId" :value="advanceId">
            {{ advanceId }}
          </option>
        </select>
      </div>
      <div>
        <label for="name">Name:</label>
        <input type="text" id="name" v-model="name" required />
      </div>
      <div>
        <label for="description">Description:</label>
        <textarea id="description" v-model="description"></textarea>
      </div>
      <div>
        <label for="status">Status:</label>
        <input type="text" id="status" v-model="status" />
      </div>
      <div>
        <label for="doc">Doc (JSON):</label>
        <textarea id="doc" v-model="doc"></textarea>
      </div>
      <div>
        <label for="test">Test (JSON):</label>
        <textarea id="test" v-model="test"></textarea>
      </div>
      <div>
        <label for="requires">Requires (IDs, comma-separated):</label>
        <input type="text" id="requires" v-model="requires" />
      </div>
      <div>
        <label for="required_by">Required By (IDs, comma-separated):</label>
        <input type="text" id="required_by" v-model="required_by" />
      </div>
      <div>
        <label for="due_date">Due Date:</label>
        <input type="date" id="due_date" v-model="due_date" />
      </div>
      <div>
        <label for="complete">Complete:</label>
        <!-- bool -->
        <input type="checkbox" id="complete" v-model="complete" />
      </div>
      <button type="submit">Submit</button>
    </form>
  </div>
</template>
